"use client";
import { AlertCircle, Check, Circle, Info, type LucideIcon, X } from "lucide-react";
import { useTheme } from "next-themes";
import { type ExternalToast, toast as sonnerToast, Toaster as Sonner } from "sonner";
import { cn } from "../../lib/utils.ts";
import { Button } from "./button.tsx";

type ToastIconConfig = Pick<ToastProps, "Icon" | "iconClassName">;

const confirmToastIconConfig: ToastIconConfig = {
  Icon: Check,
  iconClassName: "bg-green-500 border-green-700 text-primary-foreground rounded-full stroke-3",
};

const cancelToastIconConfig: ToastIconConfig = {
  Icon: X,
  iconClassName: "bg-red-500 border-red-700 text-primary-foreground rounded-full",
};

type ToasterProps = React.ComponentProps<typeof Sonner>;

export const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return <Sonner theme={theme as ToasterProps["theme"]} {...props} />;
};

interface ToastProps {
  id: string | number;
  title: string;
  /** Optional body rendered under the title — e.g. a follow-up link. */
  description?: React.ReactNode;
  Icon: LucideIcon;
  iconClassName?: string;
  actionText?: string;
  onAction?: (toastId: string | number) => void;
  /**
   * When set, the whole toast body (icon + text — everything but the dismiss X)
   * is a single click target that calls this. Use instead of an `actionText`
   * button when the entire toast should be tappable (e.g. "open the thread").
   * Supersedes `actionText`/`onAction` — when present, the separate action
   * button is not rendered, so the two never overlap.
   */
  onToastClick?: (toastId: string | number) => void;
  config?: ExternalToast;
  actionStatsigName?: string;
  /** When true, renders a dismiss X on the right that calls sonnerToast.dismiss(id). */
  dismissible?: boolean;
}

/** I recommend abstracting the toast function
 *  so that you can call it without having to use toast.custom everytime. */
export function toast(toast: Omit<ToastProps, "id">, config?: ExternalToast) {
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={toast.title}
        description={toast.description}
        Icon={toast.Icon}
        iconClassName={toast.iconClassName}
        actionText={toast.actionText}
        onAction={toast.onAction}
        onToastClick={toast.onToastClick}
        actionStatsigName={toast.actionStatsigName}
        dismissible={toast.dismissible}
      />
    ),
    config,
  );
}

/** A fully custom toast that still maintains the animations and interactions. */
function Toast({
  id,
  title,
  description,
  Icon,
  iconClassName,
  actionText,
  onAction,
  onToastClick,
  actionStatsigName,
  dismissible,
}: ToastProps) {
  // Icon + text. Rendered inside a real <button> when the toast is clickable
  // (native focus + Enter/Space — no hand-rolled role/tabindex), otherwise a
  // plain <div>. Either way the dismiss X is a separate sibling, outside the
  // tap area. Phrasing-only spans so the content is valid inside <button>.
  const body = (
    <>
      <Icon className={cn("size-5 p-1 rounded-full mr-2 shrink-0", iconClassName)} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium text-primary-foreground">{title}</span>
        {description && <span className="text-xs text-primary-foreground/80">{description}</span>}
      </span>
    </>
  );
  return (
    <div
      className="flex min-h-10 rounded-lg bg-gray-600 items-center p-3 min-w-52"
      style={{ width: "var(--width)" }}
    >
      {/* min-w-0 so a long title can't widen this row past the toast and push the X out. */}
      <div className="flex min-w-0 items-center gap-2 justify-between flex-1">
        {onToastClick ? (
          <Button
            type="button"
            variant="blank"
            size="fit"
            onClick={() => onToastClick(id)}
            data-statsig-name={actionStatsigName ?? "toast-click"}
            // Button's base styles set whitespace-nowrap for labels; a toast title
            // is prose and must wrap inside the fixed toast width, including an
            // unbroken URL or identifier (sonner sets this on the toast too, but
            // the toast's own layout shouldn't depend on the library's CSS).
            className="min-w-0 flex-1 items-center justify-start gap-0 rounded-lg text-left whitespace-normal wrap-anywhere"
          >
            {body}
          </Button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center">{body}</div>
        )}
        {!onToastClick && onAction && actionText && (
          <Button
            onClick={() => onAction(id)}
            variant="link"
            className="text-sm font-medium text-primary-foreground underline"
            data-statsig-name={actionStatsigName ?? "toast-action"}
          >
            {actionText}
          </Button>
        )}
        {dismissible && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => sonnerToast.dismiss(id)}
            aria-label="Dismiss"
            className="shrink-0 p-1 h-auto text-primary-foreground hover:bg-gray-500"
            data-statsig-name="toast-dismiss"
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export const confirmationToast = (
  title: string,
  config?: ExternalToast,
  toastProps?: Partial<Omit<ToastProps, "title">>,
) => {
  return toast(
    {
      title,
      ...confirmToastIconConfig,
      ...toastProps,
    },
    config,
  );
};

const infoToastIconConfig: ToastIconConfig = {
  Icon: Info,
  iconClassName: "text-primary-foreground",
};

/**
 * Neutral "just so you know" toast. Not a success (don't use for celebratory moments)
 * and not an error. Fits cases like "you're already on this account" where the user
 * took an action with a benign no-op outcome.
 */
export const infoToast = (
  title: string,
  config?: ExternalToast,
  toastProps?: Partial<Omit<ToastProps, "title">>,
) => {
  return toast(
    {
      title,
      ...infoToastIconConfig,
      ...toastProps,
    },
    config,
  );
};

export const archiveToast = (
  config?: ExternalToast,
  toastProps?: Partial<Omit<ToastProps, "title">>,
) => {
  return confirmationToast("Archived", config, toastProps);
};

export const eventUpdatedToast = (
  { action }: { action: "yes" | "no" | "maybe" },
  config?: ExternalToast,
) => {
  let iconConfig: ToastIconConfig;
  let text: string;
  switch (action) {
    case "yes":
      iconConfig = confirmToastIconConfig;
      text = "Event Accepted";
      break;
    case "no":
      iconConfig = cancelToastIconConfig;
      text = "Event Declined";
      break;
    case "maybe":
      iconConfig = {
        Icon: Circle,
        iconClassName: "bg-yellow-500 border-yellow-700 text-primary-foreground rounded-full",
      };
      text = "Event Tentative";
      break;
    default:
      throw new Error(`Unknown action: ${action}`);
  }
  return toast(
    {
      title: text,
      ...iconConfig,
    },
    config,
  );
};

export const emailUpdatedToast = (
  { action }: { action: "send" | "cancel" },
  config?: ExternalToast,
) => {
  let iconConfig: ToastIconConfig;
  let text: string;
  switch (action) {
    case "send":
      iconConfig = confirmToastIconConfig;
      text = "Email sent";
      break;
    case "cancel":
      iconConfig = cancelToastIconConfig;
      text = "Email cancelled";
      break;
    default:
      throw new Error(`Unknown action: ${action}`);
  }
  return toast(
    {
      title: text,
      ...iconConfig,
    },
    config,
  );
};

/** Dismiss all active toasts. Used before navigating away (e.g., starting an OAuth flow)
 *  so stale error toasts don't confuse the user when they return. */
export const dismissAllToasts = () => sonnerToast.dismiss();

/** Dismiss one toast by the id it was shown with. */
export const dismissToast = (id: string | number) => sonnerToast.dismiss(id);

export const errorToast = (
  title: string,
  config?: ExternalToast,
  toastProps?: Partial<Omit<ToastProps, "title">>,
) => {
  return toast(
    {
      title,
      Icon: AlertCircle,
      iconClassName: "bg-red-500 border-red-700 text-primary-foreground rounded-full",
      ...toastProps,
    },
    config,
  );
};
